import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { ArrowRight, Check } from 'lucide-react';

const C = { bg: '#131413', accent: '#9EAFAC', accentDark: '#586060', border: 'rgba(158,175,172,0.15)' };

export default function InvestmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isActive = user?.investment_status === 'active';
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: 'white', fontFamily: 'Inter, sans-serif' }}>
      <Navbar />
      <div style={{ paddingTop: isMobile ? '88px' : '100px', paddingBottom: '80px', padding: isMobile ? '88px 16px 80px' : '100px 24px 80px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '36px' : '64px', animation: 'fade-up 0.6s ease both' }}>
            <p style={{ color: C.accentDark, fontSize: '12px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '12px' }}>Tu Oportunidad</p>
            <h1 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: 'clamp(40px, 6vw, 72px)', letterSpacing: '-1.5px', marginBottom: '16px' }}>
              Plan{' '}
              <span style={{ color: C.accent }}>Multiplicador</span>
            </h1>
            <p style={{ color: C.accentDark, fontSize: '18px' }}>Activa tu ciclo, invita a 2 personas con tu link y haz crecer tu red por niveles.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px', alignItems: 'start' }}>

            {/* MAIN CARD with rotating border */}
            <div data-testid="investment-card" style={{ position: 'relative', gridColumn: '1 / -1', maxWidth: '480px', margin: '0 auto', width: '100%' }}>
              {/* Rotating light */}
              <div style={{
                position: 'absolute', inset: '-1px', borderRadius: '28px', overflow: 'hidden', zIndex: 0,
              }}>
                <div style={{
                  position: 'absolute', inset: '-200%',
                  background: `conic-gradient(from 0deg, transparent 0deg, transparent 300deg, ${C.accent} 340deg, transparent 360deg)`,
                  animation: 'spin 4s linear infinite',
                  transformOrigin: 'center',
                }} />
              </div>

              {/* Card body */}
              <div style={{
                position: 'relative', zIndex: 1,
                background: 'rgba(19,20,19,0.97)', borderRadius: '28px', padding: isMobile ? '24px 16px' : '40px',
                border: `1px solid ${C.border}`,
                overflow: 'hidden',
              }}>
                {/* Live badge */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, borderRadius: '50px', padding: '6px 14px', marginBottom: '28px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: C.accent, animation: 'pulse-dot 1.5s infinite' }} />
                  <span style={{ color: C.accent, fontSize: '13px', fontWeight: 600 }}>PLAN ACTIVO · EN VIVO</span>
                </div>

                <h2 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: isMobile ? '30px' : '34px', color: 'white', marginBottom: '6px', lineHeight: 1.08 }}>Plan Multiplicador</h2>
                <p style={{ color: C.accentDark, marginBottom: '32px' }}>Acceso al ciclo de crecimiento VaultGrow.</p>

                {/* Price */}
                <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'flex-end', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '18px' : '24px', marginBottom: '32px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: C.accentDark, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>Tu inversión</div>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: isMobile ? '48px' : '56px', color: 'white', lineHeight: 1 }}>$100</div>
                    <div style={{ color: C.accentDark, fontSize: '14px', marginTop: '4px' }}>USD</div>
                  </div>
                  <div style={{ textAlign: isMobile ? 'left' : 'right', flex: 1, minWidth: '120px', width: isMobile ? '100%' : 'auto' }}>
                    <div style={{ color: C.accentDark, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>Potencial del ciclo</div>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: isMobile ? '34px' : '38px', color: C.accent, lineHeight: 1, wordBreak: 'break-word' }}>$600</div>
                    <div style={{ color: C.accentDark, fontSize: '14px', marginTop: '4px' }}>al completar tu red</div>
                  </div>
                </div>

                {/* Benefits */}
                <div style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    'Activa tu ciclo con una inversión única de $100',
                    'Invita a 2 personas con tu link para iniciar tu red',
                    'Cada persona debe invitar a otras 2 para que el ciclo avance',
                    'Puedes ver el crecimiento de tu red en tiempo real',
                    'Al completar las 14 posiciones, puedes ganar hasta $600',
                    'Retiros disponibles desde 7 posiciones completadas',
                  ].map((b, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(158,175,172,0.12)', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                        <Check size={12} color={C.accent} strokeWidth={3} />
                      </div>
                      <span style={{ color: '#d1d5db', lineHeight: 1.5, wordBreak: 'break-word' }}>{b}</span>
                    </div>
                  ))}
                </div>

                {/* ROI snapshot */}
                <div style={{ background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '16px', padding: '20px', marginBottom: '32px' }}>
                  <div style={{ color: C.accentDark, fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '16px', textAlign: 'center' }}>Resumen del ciclo</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px', textAlign: 'center' }}>
                    {[['2 invitados', 'Inicio de red'], ['7 posiciones', 'Retiro desde $300'], ['14 posiciones', 'Hasta $600']].map(([l, v], i) => (
                      <div key={i} style={{
                        borderRight: !isMobile && i < 2 ? `1px solid ${C.border}` : 'none',
                        borderTop: isMobile && i > 0 ? `1px solid ${C.border}` : 'none',
                        paddingRight: !isMobile && i < 2 ? '16px' : '0',
                        paddingTop: isMobile && i > 0 ? '12px' : '0',
                      }}>
                        <div style={{ color: C.accentDark, fontSize: '12px', marginBottom: '4px' }}>{l}</div>
                        <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '18px', color: i === 2 ? C.accent : 'white' }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                {isActive ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '16px', background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, borderRadius: '16px', color: C.accent, fontWeight: 600 }}>
                    <Check size={20} />
                    Inversión activa · Ya eres miembro
                  </div>
                ) : (
                  <button
                    onClick={() => navigate('/checkout')}
                    data-testid="buy-plan-button"
                    className="btn-glow"
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                      padding: '18px', borderRadius: '16px', border: 'none',
                      background: C.accent, color: '#131413',
                      fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '18px',
                      cursor: 'pointer', transition: 'all 0.3s',
                      boxShadow: `0 0 30px rgba(158,175,172,0.25)`,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.boxShadow = '0 0 50px rgba(255,255,255,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = C.accent; e.currentTarget.style.boxShadow = '0 0 30px rgba(158,175,172,0.25)'; }}
                  >
                    Activar mi ciclo por $100 <ArrowRight size={20} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
